export function merge(a: { [key: string]: any }, b: { [key: string]: any }) {
  return Object.entries(b).reduce(
    (acc: { [key: string]: any }, [key, val]) => {
      // replace if types don't match
      if (!acc.hasOwnProperty(key) || typeof acc[key] !== typeof val) {
        acc[key] = val;
        return acc;
      }

      // concat arrays
      if (Array.isArray(val)) {
        acc[key] = acc[key].concat(val);
        return acc;
      }

      // merge objects
      if (val !== null && typeof val === "object") {
        acc[key] = merge(acc[key], val);
        return acc;
      }

      // replace everything else
      acc[key] = val;
      return acc;
    },
    { ...a }
  );
}

export function diff(a: { [key: string]: any }, b: { [key: string]: any }) {
  return Object.entries(b).reduce((acc: { [key: string]: any }, [key, val]) => {
    // include if types don't match
    if (!a.hasOwnProperty(key) || typeof a[key] !== typeof val) {
      acc[key] = val;
      return acc;
    }

    // include if array elements don't match
    if (Array.isArray(val)) {
      if (a[key].length !== val.length || !a[key].every((i: any) => val.includes(i))) {
        acc[key] = val;
      }
      return acc;
    }

    // diff nested objects
    if (val !== null && typeof val === "object") {
      const obj = diff(a[key], val);
      if (Object.keys(obj).length) {
        acc[key] = obj;
      }
      return acc;
    }

    // include mismatched values
    if (a[key] !== val) acc[key] = val;
    return acc;
  }, {});
}

export function deepmerge(...objs: { [key: string]: any }[]) {
  return objs.filter((o) => typeof o === "object").reduce(merge, {});
}

export function clone<T = { [key: string]: any }>(inObject: T): T {
  let outObject: any, value, key;
  if (typeof inObject !== "object" || inObject === null) {
    return inObject; // Return the value if inObject is not an object
  }

  if (inObject instanceof Date) {
    return new Date(inObject) as any;
  }

  // Create an array or object to hold the values
  outObject = Array.isArray(inObject) ? [] : {};

  for (key in inObject) {
    value = (inObject as any)[key];

    // Recursively (deep) copy for nested objects, including arrays
    outObject[key] = clone(value);
  }

  return outObject as T;
}

export function walk(path: string, obj: any) {
  return path.split(".").reduce((o, i) => o[i], obj);
}

export function patch(path: string, value: any) {
  return path
    .split(".")
    .reverse()
    .reduce((a, k) => ({ [k]: a }), value);
}

export function setValue<T = any>(obj: T, path: string, value: any): T {
  const cloned = clone(obj);
  const pList = path.split(".");
  const key: any = pList.pop();
  const pointer: any = pList.reduce((accumulator: any, currentValue) => {
    if (accumulator[currentValue] === undefined) accumulator[currentValue] = {};
    return accumulator[currentValue];
  }, cloned);
  pointer[key] = value;
  return cloned;
}

export default {
  clone,
  merge,
  diff,
  deepmerge,
  walk,
  patch,
};
